import { Table, ActionIcon, Menu, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconDotsVertical, IconEdit, IconTrash, IconActivity, IconPerspectiveOff } from '@tabler/icons-react';

import { useUpdateShop } from '../hooks/use-update-shop';
import { useDeleteShop } from '../hooks/use-delete-shop';
import { useShopStatus } from '../hooks/use-shop-status';
import { convertShopsToTable } from '../utils/helpers/converter';
import { useResponsive, CustomTable, CustomCopyBtn, CustomTableMobile } from '@/shared';
import { useGetConfigBaseUrlQuery } from '@/modules/config';
import { EditShopForm } from './edit-shop-form';
import { ShopBalance } from './shop-balance';
import {
  partnerCommissionCalculationBaseTitles,
  shopStatusTitles,
  shopTypeTitles,
} from '../utils/const/common';
import { getReferralLink } from '../utils/helpers/referral-link';

import type { TShop } from '../store/types';

type Props = {
  items: TShop[];
  isLoading?: boolean;
};

const getPartnerCommissions = (item: TShop) => item.partnerCommissions
  .map((commission) => (
    `${commission.currency}: ${commission.commissionPercent}% (${partnerCommissionCalculationBaseTitles[commission.calculationBase]})`
  ))
  .join(', ') || '-';

export const ShopsTable = (props: Props) => {
  const { items } = props;
  const { isDesktop } = useResponsive();
  const { data: baseUrlConfig } = useGetConfigBaseUrlQuery();

  const { handleShopEdit } = useUpdateShop();
  const { openDeleteModal } = useDeleteShop();
  const {
    activateShop,
    deactivateShop,
  } = useShopStatus();

  const renderReferralLink = (item: TShop, leftIcon = false) => {
    const referralLink = getReferralLink(baseUrlConfig?.baseUrl, item.referralCode);
    if (!referralLink) return '-';

    return (
      <CustomCopyBtn
        text={referralLink}
        tooltipPosition="top"
        leftIcon={leftIcon}
        rightIcon={!leftIcon}
      />
    );
  };

  const openEditModal = (item: TShop) => modals.open({
    title: `Редактирование магазина ${item.name}`,
    centered: true,
    children: (
      <EditShopForm
        shop={item}
        onSubmit={handleShopEdit}
        onClose={modals.closeAll}
      />
    ),
  });

  const getActions = (item: TShop) => (
    <Menu position="bottom-end" shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon variant="subtle">
          <IconDotsVertical size={16} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        {item.status === 'active'
          ? (
            <Menu.Item
              leftSection={<IconPerspectiveOff size={14} />}
              color="yellow"
              onClick={() => deactivateShop({ shopId: item.id })}
            >
              Деактивировать
            </Menu.Item>

          )
          : (
            <Menu.Item
              leftSection={<IconActivity size={14} />}
              color="green"
              onClick={() => activateShop({ shopId: item.id })}
            >
              Активировать
            </Menu.Item>
          )
        }
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconEdit size={14} />}
          onClick={() => openEditModal(item)}
        >
          Редактировать
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          color="red"
          leftSection={<IconTrash size={14} />}
          onClick={() => openDeleteModal(item)}
        >
          Удалить
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  const titles = [
    'ID',
    'Название',
    'Тип',
    'Баланс',
    'Партнёрские комиссии',
    'Реферальная ссылка',
    'Создан',
    'Токен',
    'Статус',
    'URL колбэка',
  ];

  const rows = convertShopsToTable(items).map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>{item.id}</Table.Td>
      <Table.Td>{item.name}</Table.Td>
      <Table.Td>{shopTypeTitles[item.type]}</Table.Td>
      <Table.Td>
        <ShopBalance shop={item} />
      </Table.Td>
      <Table.Td>{getPartnerCommissions(item)}</Table.Td>
      <Table.Td>{renderReferralLink(item)}</Table.Td>
      <Table.Td>{item.created}</Table.Td>
      <Table.Td>
        <CustomCopyBtn text={item.token} tooltipPosition="top" rightIcon />
      </Table.Td>
      <Table.Td>{shopStatusTitles[item.status]}</Table.Td>
      <Table.Td>
        <p style={{ width: '100px', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
          {item.callbackUrl}
        </p>
      </Table.Td>
      <Table.Td width={50}>{getActions(item)}</Table.Td>
    </Table.Tr>
  ));

  const rowsMobile = convertShopsToTable(items).map((item) => ({
    key: item.id,
    headerActions: getActions(item),
    items: [
      // { name: 'ID', value: item.id },
      // { name: 'Название', value: <Text truncate="end" size="xs">{item.name}</Text> },
      { name: '', value: <Text truncate="end" size="xs" fw={'bolder'} mb={3}>{item.name} ({item.id})</Text> },
      { name: 'Тип', value: shopTypeTitles[item.type] },
      { value: <ShopBalance shop={item} withAddBtn={false} notZeroBalances /> },
      { value: <ShopBalance shop={item} /> },
      { name: 'Партнёрские комиссии', value: getPartnerCommissions(item) },
      {
        name: 'Реферальная ссылка',
        value: renderReferralLink(item, true),
      },
      { name: 'Создан', value: item.created },
      { name: 'Токен', value: <CustomCopyBtn text={item.token} tooltipPosition="top" leftIcon /> },
      { name: 'Статус', value: shopStatusTitles[item.status] },
      { name: 'URL колбэка', value: <Text truncate="end" size="xs">{item.callbackUrl}</Text> },
    ],
  }));

  return isDesktop
    ? (
      <CustomTable
        {...props}
        titles={titles}
        rows={rows}
        isPaginated={false}
      />
    )
    : (
      <CustomTableMobile
        rows={rowsMobile}
        headerPointsCount={2}
        isPaginated={false}
        {...props}
      />
    );
};
